<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox 
          title="Contact Overview"
          :loading="loading"
          :avatarImage="logoURL"
          :avatarName="contactData.name"
          :avatarCircleMode="false"
          :avatarEditable="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.contact)"
          @editImage="editAvatarImage"
          @removeImage="removeAvatarImage">
          
          <h4>{{contactData.name}}</h4>
          <p>
            <span class="label label-supplier mr-2" v-if="contactData.isSupplier"><i class="fas fa-industry"></i> supplier</span>
            <span class="label label-customer" v-if="contactData.isCustomer"><i class="fas fa-user-circle"></i> customer</span>
          </p>
          <template v-if="contactData.labelList && contactData.labelList.length > 0">
            <h5>Labels</h5>
            <p>
              <span class="label label-empty label-overview-box mr-1" v-for="label in contactData.labelList" :key="label.id"><i class="fas fa-circle pr-1" :style="{color: label.colour}"> </i> {{label.name}}</span>
            </p>
          </template>
          <template v-if="hasIntegrations">
            <h5>Integrations</h5>
            <p>
              <LinkIntegrationLink
                v-for="integration in integrationList"
                :key="integration.code"
                :integration="integration"
                @relink="relinkIntegration($event)"
                @refresh="reloadData()" />
            </p>
          </template>
          <OverviewBoxInfoItem v-if="defaultContact" icon="fa-user">{{defaultContact}}</OverviewBoxInfoItem>
          <OverviewBoxInfoItem v-if="contactData.phone" icon="fa-phone">{{contactData.phone}}</OverviewBoxInfoItem>
          <OverviewBoxInfoItem v-if="defaultAddress && defaultAddress.formattedAddress" icon="fa-map-marker-alt">
            <a :href="'http://maps.google.com?q=' + defaultAddress.formattedAddress" target="_blank"> {{defaultAddress.formattedAddress}}</a>
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem v-if="contactData.website" icon="fa-globe-asia"><a :href="contactData.website" target="_blank"> Website</a></OverviewBoxInfoItem>

          <OverviewBoxList>
            <OverviewBoxListItem title="Status">
              <span v-if="contactData.isActive" class="status-active">Active</span>
              <span v-if="!contactData.isActive" class="status-archive">Archived</span>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Credit Type" v-if="contactData.isCustomer">
              <span v-if="isCreditTypeAccount" :class="[creditTypeClass]">{{creditTypeName}}</span>
              <span v-if="!isCreditTypeAccount">{{creditTypeName}}</span>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Credit Limit" v-if="isCreditTypeAccount && userHasReadPermission(PermissionTypes.ContactPermissionTypes.customerCredit)">{{contactData.credit.creditLimit | formatCurrency}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Minimum Spend" v-if="isCreditTypeAccount && userHasReadPermission(PermissionTypes.ContactPermissionTypes.customerCredit) && contactData.credit.minSpendRequired > 0">{{contactData.credit.minSpendRequired | formatCurrency}} / mo</OverviewBoxListItem>
            <OverviewBoxListItem title="Available Balance" v-if="isCreditTypeAccount && userHasReadPermission(PermissionTypes.ContactPermissionTypes.customerCredit)">
              <span :class="contactData.credit.availableCredit <= 0 ? 'financial-loss' : 'financial-profit'">{{contactData.credit.availableCredit | formatCurrency}}</span>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Payment Terms" v-if="isCreditTypeAccount && userHasReadPermission(PermissionTypes.ContactPermissionTypes.customerCredit)">{{creditPaymentTerms}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Account Manager" v-if="contactData.manageUser">
              <LinkUser :id="contactData.manageUser.id" :name="contactData.manageUser.name" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{contactData.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">{{contactData.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>
      </div>
      <div class="col-sm-10">
        <TabbedBox :loading="loading">
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-summary">
              <i class="far fa-analytics"></i> Summary
            </Tab>
            <Tab tabid="tab-details" ref="contactDetailTab">
              <i class="far fa-id-card-alt"></i> Contact Details
            </Tab>
            <Tab tabid="tab-2">
              <i class="far fa-address-book"></i> Addresses
            </Tab>
            <Tab tabid="tab-3">
              <i class="far fa-user-friends"></i> People
            </Tab>
            <Tab tabid="tab-4" v-if="contactData.isCustomer && userHasReadPermission(PermissionTypes.ContactPermissionTypes.customerCredit)">
              <i class="far fa-credit-card"></i> Credit Terms
            </Tab>
            <Tab tabid="tab-5" v-if="contactData.isSupplier && userHasReadPermission(PermissionTypes.ContactPermissionTypes.supplierCredit)">
              <i class="far fa-industry"></i> Supplier Details
            </Tab>
            <Tab tabid="tab-6" v-if="isCustomerPricingAvailable">
              <i class="far fa-dollar-sign"></i> Customer Pricing
            </Tab>
            <Tab tabid="tab-8">
              <i class="far fa-exchange"></i> Transactions
            </Tab>
            <Tab tabid="tab-9">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>

          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-summary">
              <div v-if="contactData.id">
                <h1 class="mt-2 mb-4">Overview for {{contactData.name}}</h1>
                <AnalyticsContactDashboard :contactId="contactData.id" :creditType="contactData.credit ? contactData.credit.creditType : null" />
              </div>
            </TabPage>
            <TabPage tabid="tab-details">
              <DataPageArea title="Details">
                <template slot="actionArea">
                  <ActionDropMenu class="float-right">
                    <DropdownMenuItem @click="editContact" icon="fa-pencil" v-if="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.contact)">Edit Contact</DropdownMenuItem>
                    <DropdownMenuItem @click="editDespatch" icon="fa-truck" v-if="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.contact)">Edit Despatch Details</DropdownMenuItem>
                    <DropdownMenuDivider v-if="userHasUpdatePermission(PermissionTypes.AccountsPermissionTypes.accountReports)" />
                    <DropdownMenuItem @click="sendAccountStatement" icon="fa-paper-plane" v-if="userHasUpdatePermission(PermissionTypes.AccountsPermissionTypes.accountReports)">Send Account Statement</DropdownMenuItem>
                    <DropdownMenuDivider />
                    <DropdownMenuItem @click="archiveContact" v-if="contactData.isActive && userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.contact)" icon="fa-archive">Archive Contact</DropdownMenuItem>
                    <DropdownMenuItem @click="restoreContact" v-if="!contactData.isActive && userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.contact)" icon="fa-undo">Restore Contact</DropdownMenuItem>
                  </ActionDropMenu>
                </template>
                <template slot="dataArea">
                  <DataItem title="Name">{{contactData.name}}</DataItem>
                  <DataItem title="Default Address">{{defaultAddress ? defaultAddress.formattedAddress : ''}}</DataItem>
                  <DataItem title="Default Contact">{{defaultContact}}</DataItem>

                  <DataItem title="Phone">{{contactData.phone}}</DataItem>
                  <DataItem title="Fax">{{contactData.fax}}</DataItem>
                  <DataItem title="Notes">{{contactData.note}}</DataItem>

                  <DataItem title="Website">{{contactData.website}}</DataItem>
                  <DataItem title="Account Manager">{{contactData.manageUser ? contactData.manageUser.name : ''}}</DataItem>
                  <DataItem></DataItem>

                  <DataItem title="Default Despatch">
                    {{contactData.defaultDespatchType | formatLookupName(DespatchTypeLookup)}}
                    <template v-if="contactData.defaultDespatchType == DespatchTypes.delivery && contactData.defaultDeliveryStock">
                      <br />{{contactData.defaultDeliveryStock.code}} - {{contactData.defaultDeliveryStock.name}}
                    </template>
                  </DataItem>
                </template>
              </DataPageArea>
            </TabPage>

            <TabPage tabid="tab-2">
              <div class="row">
                <div class="col-lg-12">
                  <div v-if="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.contact)" style="clear: both;">
                    <ActionButton type="new" class="mb-1 btn-xs float-right" @click="newAddress"> New Address</ActionButton>
                  </div>
                  <h3>Address List</h3>
                </div>
              </div>
                
              <StaticDataTable class="static-table-list">
                <template v-slot:header>
                  <tr>
                    <th></th>
                    <th>Address</th>
                    <th>Suburb</th>
                    <th>Postcode</th>
                    <th>State</th>
                    <th>Country</th>
                    <th>Labels</th>
                    <th>Delivery Tag</th>
                    <th>Actions</th>
                  </tr>
                </template>
                <template v-slot:body>
                  <tr v-for="address in contactData.addresses" :key="address.id">
                    <td><i class="fas fa-star default-list-item" v-if="contactData.defaultAddressId == address.id"></i></td>
                    <td>{{address.address1}} {{address.address2}}</td>
                    <td>{{address.suburb}}</td>
                    <td>{{address.postcode}}</td>
                    <td>{{address.state}}</td>
                    <td>{{address.country}}</td>
                    <td><LabelTags :labelList="address.labelList" /></td>
                    <td><LabelTags :labelList="address.deliveryLabelList" /></td>
                    <td>
                      <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.contact)" :tableAction="true">
                        <DropdownMenuItem @click="updateDefaultAddress(address.id)" icon="fa-star" :disabled="contactData.defaultAddressId == address.id">Make Default</DropdownMenuItem>
                        <DropdownMenuDivider />
                        <DropdownMenuItem @click="editAddress(address)" icon="fa-pencil">Edit Address</DropdownMenuItem>
                        <DropdownMenuItem @click="deleteAddress(address.id)" icon="fa-trash-alt">Delete Address</DropdownMenuItem>
                      </ActionDropMenu>
                    </td>
                  </tr>
                </template>
              </StaticDataTable>
            </TabPage>

            <TabPage tabid="tab-3">
              <div class="row">
                <div class="col-lg-12">
                  <div v-if="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.contact)" style="clear: both;">
                    <ActionButton type="new" class="mb-1 btn-xs float-right" @click="newPerson"> New Person</ActionButton>
                  </div>
                  <h3>People List</h3>
                </div>
              </div>
              <StaticDataTable class="static-table-list">
                <template v-slot:header>
                  <tr>
                    <th></th>
                    <th>Title</th>
                    <th>Name</th>
                    <th>Position</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Mobile</th>
                    <th>Notes</th>
                    <th>Labels</th>
                    <th>Actions</th>
                  </tr>
                </template>
                <template v-slot:body>
                  <tr v-for="person in contactData.persons" :key="person.id">
                    <td><i class="fas fa-star default-list-item" v-if="contactData.defaultPersonId == person.id"></i></td>
                    <td>{{person.title}}</td>
                    <td>{{person.firstName}} {{person.lastName}}</td>
                    <td>{{person.position}}</td>
                    <td>{{person.email}}</td>
                    <td>{{person.phone}}</td>
                    <td>{{person.mobile}}</td>
                    <td>{{person.note}}</td>
                    <td><LabelTags :labelList="person.labelList" /></td>
                    <td>
                      <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.contact)" :tableAction="true">
                        <DropdownMenuItem @click="updateDefaultPerson(person.id)" icon="fa-star" :disabled="contactData.defaultPersonId == person.id">Make Default</DropdownMenuItem>
                        <DropdownMenuDivider />
                        <DropdownMenuItem @click="editPerson(person)" icon="fa-pencil">Edit Person</DropdownMenuItem>
                        <DropdownMenuItem @click="deletePerson(person.id)" icon="fa-trash-alt">Delete Person</DropdownMenuItem>
                      </ActionDropMenu>
                    </td>
                  </tr>
                </template>
              </StaticDataTable>
            </TabPage>

            <TabPage tabid="tab-4" v-if="contactData.isCustomer">
              <DataPageArea title="Customer Credit Terms">
                <template slot="actionArea">
                  <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.customerCredit)" class="float-right">
                    <DropdownMenuItem @click="editCreditStatus" icon="fa-credit-card">Edit Credit Status</DropdownMenuItem>
                    <DropdownMenuItem @click="editCreditDetails" icon="fa-pencil">Edit Credit Details</DropdownMenuItem>
                  </ActionDropMenu>
                </template>
                <template slot="dataArea">
                  <template v-if="!isCreditTypeAccount">
                    <DataItem title="Credit Type">{{creditType}}</DataItem>
                  </template>
                  <template v-if="isCreditTypeAccount">
                    <DataItem title="Credit Type">{{creditType}}</DataItem>
                    <DataItem title="Credit Status">{{creditStatus}}</DataItem>
                    <DataItem title="Credit Limit">{{contactData.credit.creditLimit | formatCurrency}}</DataItem>
                    <DataItem title="Minimum Monthly Spend">{{contactData.credit.minSpendRequired > 0 ? formatCurrency(contactData.credit.minSpendRequired) : 'None'}}</DataItem>
                    <DataItem title="Min Spend Admin Fee">{{contactData.credit.minSpendAdminFee > 0 ? formatCurrency(contactData.credit.minSpendAdminFee) : 'None'}}</DataItem>
                    <DataItem title="Payment Terms">{{creditPaymentTerms}}</DataItem>
                    <DataItem title="Entity Type">{{entityType}}</DataItem>
                    <DataItem title="Trading Name">{{contactData.credit.tradingName}}</DataItem>
                    <DataItem title="ABN">{{contactData.credit.abn}}</DataItem>
                    <DataItem title="Legal Name" v-show="shouldShowLegalName()">{{contactData.credit.legalName}}</DataItem>

                    <template v-if="shouldShowCompanyFields()">
                        <DataItem title="State of Incorporation">{{contactData.credit.incorpState}}</DataItem>
                        <DataItem title="Incorporation Date">{{contactData.credit.incorpDate | formatDate}}</DataItem>
                        <DataItem title="ACN">{{contactData.credit.acn}}</DataItem>
                    </template>

                    <template v-if="isCreditTrustType()">
                      <DataItem title="Trust Name">{{contactData.credit.trustName}}</DataItem>
                      <DataItem title="Trust Commencement Date">{{contactData.credit.trustCommence | formatDate}}</DataItem>
                      <DataItem title="Corporate Trustee">{{contactData.credit.isCorporateTrustee | formatBoolean}}</DataItem>
                    </template>

                    <DataItem title="Personal Guarantee">{{contactData.credit.personalGuarantee}}</DataItem>
                    <DataItem title="Guarantee Signed Date">{{contactData.credit.personalGuaranteeDateSigned | formatDate}}</DataItem>
                    <DataItem title="Veda Search">{{contactData.credit.vedaSearch | formatDate}}</DataItem>
                    <DataItem title="Account Opened">{{contactData.credit.accountOpened | formatDate}}</DataItem>
                    <DataItem title="Credit Application Date">{{contactData.credit.creditApplicationDate | formatDate}}</DataItem>

                    <template v-if="isCreditPropertySecured()">
                      <DataItem title="PPS GONI">{{contactData.credit.personalPropGONI}}</DataItem>
                      <DataItem title="PPS Token">{{contactData.credit.personalPropToken}}</DataItem>
                      <DataItem title="PPS Registration">{{contactData.credit.personalPropRegistration}}</DataItem>
                      <DataItem title="PPS Expiry">{{contactData.credit.personalPropExpiry | formatDate}}</DataItem>
                    </template>
                  </template>
                </template>
              </DataPageArea>

              <div class="m-b-sm">
                <br>
              </div>
              <div class="row" style="border-top: 1px solid #e0e0e0;">
                <div class="col-lg-8 p-2">
                  <ActivityLogStream ref="creditActivityLog" :url="creditActivityStreamURL" :linkType="LinkTypes.contactCreditInfo" :linkId="contactData.credit.id">
                    <template v-slot:title><h3>Recent Credit Activity</h3></template>
                  </ActivityLogStream>
                </div>
                <div class="col-lg-4">
                  <DocumentList title="Credit Documents" ref="creditDocumentList" :linkType="creditInfoLinkType" :linkId="contactData.credit.id" :permissionResource="PermissionTypes.ContactPermissionTypes.contact" class="mt-2">
                  </DocumentList>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-5" v-if="contactData.isSupplier">
              <DataPageArea title="Supplier Details">
                <template slot="actionArea">
                  <ActionDropMenu class="float-right">
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.supplierCredit)" @click="editSupplierDetails" icon="fa-industry">Edit Supplier Details</DropdownMenuItem>
                    <DropdownMenuDivider v-if="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.supplierPricing)" />
                    <DropdownMenuItem @click="displaySupplierPricingReport" icon="fa-clipboard-list">Supplier Pricing Report</DropdownMenuItem>
                  </ActionDropMenu>
                </template>
                <template slot="dataArea">
                  <DataItem title="Supplier Type">{{supplierType}}</DataItem>
                  <DataItem title="Supplier Payment Terms">{{supplierPaymentTerms}}</DataItem>
                  <DataItem title="Landing Cost">{{supplierLandingCost}}</DataItem>
                  <DataItem title="Currency">{{contactData.supplierCurrencyCode}}</DataItem>
                  <DataItem title="Tax Type">{{supplierTaxType}}</DataItem>
                  <DataItem title="Supplier Delivery Lead">{{supplierDeliveryLead}}</DataItem>
                </template>
              </DataPageArea>
              
              <div v-if="userHasReadPermission(PermissionTypes.ContactPermissionTypes.supplierPricing)">
                <div class="row mt-4 mb-1">
                  <div class="col-lg-12">
                    <h3>Supplier Pricing</h3>
                  </div>
                </div>

                <ul class="nav nav-tabs" role="tablist">
                  <Tab :first="true" tabid="tab-supplier-pricing-1">
                    <i class="far fa-container-storage"></i> Bulk Glass
                  </Tab>
                  <Tab tabid="tab-supplier-pricing-2">
                    <i class="far fa-cogs"></i> Processed Glass
                  </Tab>
                  <Tab tabid="tab-supplier-pricing-3">
                    <i class="far fa-tools"></i> Services
                  </Tab>
                  <Tab tabid="tab-supplier-pricing-4">
                    <i class="far fa-box-up"></i> General
                  </Tab>
                </ul>
                <div class="tab-content">
                  <TabPage :first="true" tabid="tab-supplier-pricing-1">
                    <div class="row">
                      <div class="col-lg-12">
                        <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.supplierPricing) && !editingBulkPricing" class="float-right">
                          <DropdownMenuItem @click="editingBulkPricing = true" icon="fa-pencil">Edit Bulk Pricing</DropdownMenuItem>
                        </ActionDropMenu>
                        <h3>Bulk Pricing</h3>
                      </div>
                    </div>
                    <div class="row mt-2">
                      <div class="col-lg-12">
                        <FormGroup>
                          <SupplierBulkPriceList 
                            :contactId="contactData.id" 
                            :supplierCurrencyCode="contactData.supplierCurrencyCode"
                            :supplierBulkPricingData="supplierBulkPricingData"
                            :editing="editingBulkPricing"
                            @update="supplierBulkPricingData = $event"
                            @refreshData="reloadData()"
                          />
                          <div v-if="editingBulkPricing" class="stock-price-editing-area">
                            <FormHorizontalRule />
                            <FormSubmit label="Update" :submitting="bulkPricingSubmitting" @cancelled="cancelPriceEditing" @submitted="submitBulkPricingEdit"></FormSubmit>
                          </div>
                        </FormGroup>
                      </div>
                    </div>
                  </TabPage>
                  <TabPage tabid="tab-supplier-pricing-2">
                    <div class="row">
                      <div class="col-lg-12">
                        <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.supplierPricing) && !editingProcessedPricing" class="float-right">
                          <DropdownMenuItem @click="editingProcessedPricing = true" icon="fa-pencil">Edit Processed Pricing</DropdownMenuItem>
                        </ActionDropMenu>
                        <h3>Processed Pricing</h3>
                      </div>
                    </div>
                    <div class="row mt-2">
                      <div class="col-lg-12">
                        <FormGroup>
                          <SupplierProcessedPriceList 
                            :contactId="contactData.id" 
                            :supplierCurrencyCode="contactData.supplierCurrencyCode"
                            :supplierProcessedPricingData="supplierProcessedPricingData"
                            :editing="editingProcessedPricing"
                            @update="supplierProcessedPricingData = $event"
                            @refreshData="reloadData()"
                          />
                          <div v-if="editingProcessedPricing" class="stock-price-editing-area">
                            <FormHorizontalRule />
                            <FormSubmit label="Update" :submitting="processedPricingSubmitting" @cancelled="cancelPriceEditing" @submitted="submitProcessedPricingEdit"></FormSubmit>
                          </div>
                        </FormGroup>
                      </div>
                    </div>
                  </TabPage>
                  <TabPage tabid="tab-supplier-pricing-3">
                    <div class="row">
                      <div class="col-lg-12">
                        <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.supplierPricing) && !editingServicePricing" class="float-right">
                          <DropdownMenuItem @click="editingServicePricing = true" icon="fa-pencil">Edit Service Pricing</DropdownMenuItem>
                        </ActionDropMenu>
                        <h3>Service Pricing</h3>
                      </div>
                    </div>
                    <div class="row mt-2">
                      <div class="col-lg-12">
                        <FormGroup>
                          <SupplierServicePriceList 
                            :contactId="contactData.id" 
                            :supplierCurrencyCode="contactData.supplierCurrencyCode"
                            :supplierServicePricingData="supplierServicePricingData"
                            :editing="editingServicePricing"
                            @update="supplierServicePricingData = $event"
                            @refreshData="reloadData()"
                          />
                          <div v-if="editingServicePricing" class="stock-price-editing-area">
                            <FormHorizontalRule />
                            <FormSubmit label="Update" :submitting="servicePricingSubmitting" @cancelled="cancelPriceEditing" @submitted="submitServicePricingEdit"></FormSubmit>
                          </div>
                        </FormGroup>
                      </div>
                    </div>
                  </TabPage>
                  <TabPage tabid="tab-supplier-pricing-4">
                    <div class="row">
                      <div class="col-lg-12">
                        <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.supplierPricing) && !editingGeneralPricing" class="float-right">
                          <DropdownMenuItem @click="editingGeneralPricing = true" icon="fa-pencil">Edit General Pricing</DropdownMenuItem>
                        </ActionDropMenu>
                        <h3>General Pricing</h3>
                      </div>
                    </div>
                    <div class="row mt-2">
                      <div class="col-lg-12">
                        <FormGroup>
                          <SupplierGeneralPriceList 
                            :contactId="contactData.id" 
                            :supplierCurrencyCode="contactData.supplierCurrencyCode"
                            :supplierGeneralPricingData="supplierGeneralPricingData"
                            :editing="editingGeneralPricing"
                            @update="supplierGeneralPricingData = $event"
                            @refreshData="reloadData()"
                          />
                          <div v-if="editingGeneralPricing" class="stock-price-editing-area">
                            <FormHorizontalRule />
                            <FormSubmit label="Update" :submitting="generalPricingSubmitting" @cancelled="cancelPriceEditing" @submitted="submitGeneralPricingEdit"></FormSubmit>
                          </div>
                        </FormGroup>
                      </div>
                    </div>
                  </TabPage>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-6" v-if="isCustomerPricingAvailable">
              <ul class="nav nav-tabs" role="tablist">
                <Tab :first="true" tabid="tab-customer-pricing-1">
                  <i class="far fa-tag"></i> Active Pricing
                </Tab>
                <Tab tabid="tab-customer-pricing-2">
                  <i class="far fa-archive"></i> Archived Price Lists
                </Tab>
              </ul>
              <div class="tab-content">
                <TabPage :first="true" tabid="tab-customer-pricing-1">
                  <DataPageArea title="Customer Pricing" subTitle="Customer pricing details and price lists">
                    <template slot="actionArea">
                      <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.customerPricing)" class="float-right">
                        <DropdownMenuItem @click="editCustomerPricingDetails()" icon="fa-pencil">Edit Pricing Details</DropdownMenuItem>
                        <DropdownMenuDivider />
                        <DropdownMenuItem @click="newCustomerPriceList()" icon="fa-plus">New Customer Price List</DropdownMenuItem>
                        <DropdownMenuItem @click="viewCustomerPriceListConfig()" icon="fa-cog">Customer Price List Configuration</DropdownMenuItem>
                      </ActionDropMenu>
                    </template>

                    <template slot="dataArea">
                      <DataItem title="Default Sales Rate">{{defaultSalesRate}}</DataItem>
                    </template>
                  </DataPageArea>

                  <div class="row mt-4">
                    <div class="col-lg-12">

                      <ContentTitle>Customer Price Lists</ContentTitle>
                      <ContentSubTitle>Active or scheduled price lists for the customer</ContentSubTitle>

                      <StaticDataTable class="static-table-list">
                        <template v-slot:header>
                          <tr>
                            <th>Status</th>
                            <th>Start Date</th>
                            <th>Created By</th>
                            <th>Actions</th>
                          </tr>
                        </template>
                        <template v-slot:body>
                          <tr v-for="priceList in customerCurrentPriceLists" :key="priceList.id">
                            <td>
                              <span class="label label-status-active" v-if="priceList.isActive">Active</span>
                              <span class="label label-status-pending" v-if="priceList.isScheduled">Scheduled</span>
                              <span class="label label-status-archive" v-if="priceList.isArchived">Archived</span>
                              <span class="label label-status-waiting ml-2" v-if="!priceList.isApproved">Pending Approval</span>
                            </td>
                            <td>
                              {{priceList.isScheduled ? priceList.scheduleStartDate : priceList.activeStartedDate | formatDate}}
                            </td>
                            <td><LinkUser :id="priceList.user.id" :name="priceList.user.name" /></td>
                            <td>
                              <ActionDropMenu :tableAction="true">
                                <DropdownMenuItem @click="displayCustomerPriceList(priceList.id)" icon="fa-eye" label="Price List">Price List</DropdownMenuItem>
                              </ActionDropMenu>
                            </td>
                          </tr>
                        </template>
                      </StaticDataTable>
                      <p class="text-center font-italic" v-if="customerCurrentPriceLists.length == 0">No price lists</p>
                    </div>
                  </div>
                </TabPage>
                <TabPage tabid="tab-customer-pricing-2">
                  <div class="row">
                    <div class="col-lg-12">
                      <ContentTitle>Archived Price Lists</ContentTitle>
                      <ContentSubTitle>Old archived price lists no longer active</ContentSubTitle>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-lg-12">
                      <StaticDataTable class="static-table-list">
                        <template v-slot:header>
                          <tr>
                            <th>Status</th>
                            <th>Date Range</th>
                            <th>Created By</th>
                            <th>Actions</th>
                          </tr>
                        </template>
                        <template v-slot:body>
                          <tr v-for="priceList in customerCompletePriceLists" :key="priceList.id">
                            <td>
                              <span class="label label-status-active" v-if="priceList.isActive">Active</span>
                              <span class="label label-status-pending" v-if="priceList.isScheduled">Scheduled</span>
                              <span class="label label-status-archive" v-if="priceList.isArchived">Archived</span>
                            </td>
                            <td>
                              <span v-if="priceList.snapshotStartDate">{{priceList.snapshotStartDate | formatDate}} to {{priceList.snapshotEndDate | formatDate}}</span>
                              <span v-if="!priceList.snapshotStartDate">Unknown</span>
                            </td>
                            <td><LinkUser v-if="priceList.user" :id="priceList.user.id" :name="priceList.user.name" /></td>
                            <td>
                              <ActionDropMenu :tableAction="true">
                                <DropdownMenuItem @click="displayCustomerPriceListReport(priceList.id)" icon="fa-clipboard-list" label="Archived Report">View Archived Report</DropdownMenuItem>
                              </ActionDropMenu>
                            </td>
                          </tr>
                        </template>
                      </StaticDataTable>
                      <p class="text-center font-italic" v-if="customerCompletePriceLists.length == 0">No archived price lists</p>
                    </div>
                  </div>
                </TabPage>
              </div>
            </TabPage>

            <TabPage tabid="tab-8">
              <ul class="nav nav-tabs" role="tablist">
                <Tab :first="contactData.isCustomer" tabid="tab-customer-transaction-order" v-if="userHasReadPermission(PermissionTypes.OrderPermissionTypes.order) && contactData.isCustomer">
                  <i class="far fa-shopping-cart"></i> Orders
                </Tab>
                <Tab tabid="tab-customer-transaction-invoice" v-if="userHasReadPermission(PermissionTypes.AccountsPermissionTypes.invoice) && contactData.isCustomer">
                  <i class="far fa-envelope-open-dollar"></i> Invoices
                </Tab>
                <Tab tabid="tab-customer-transaction-payment" v-if="userHasReadPermission(PermissionTypes.AccountsPermissionTypes.payment) && contactData.isCustomer">
                  <i class="far fa-usd-circle"></i> Payments
                </Tab>
                <Tab tabid="tab-customer-transaction-overpayment" v-if="userHasReadPermission(PermissionTypes.AccountsPermissionTypes.payment) && contactData.isCustomer">
                  <i class="far fa-sack-dollar"></i> Over Payments
                </Tab>
                <Tab tabid="tab-customer-transaction-creditnote" v-if="userHasReadPermission(PermissionTypes.AccountsPermissionTypes.creditNote) && contactData.isCustomer">
                  <i class="far fa-money-check-alt"></i> Credit Notes
                </Tab>
                <Tab :first="!contactData.isCustomer" tabid="tab-customer-transaction-po" v-if="userHasReadPermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrder) && contactData.isSupplier">
                  <i class="far fa-dolly-flatbed"></i> Purchase Orders
                </Tab>
              </ul>
              <div class="tab-content">
                <TabPage :first="contactData.isCustomer" tabid="tab-customer-transaction-order" v-if="userHasReadPermission(PermissionTypes.OrderPermissionTypes.order) && contactData.isCustomer">
                  <div class="row">
                    <div class="col-lg-12">
                      <OrderDataTable :filterContactId="id" :openNewTab="true">
                        <template v-slot:title>
                          <h3>Order List for {{contactData.name}}</h3>
                        </template>
                      </OrderDataTable>
                    </div>
                  </div>
                </TabPage>
                <TabPage tabid="tab-customer-transaction-invoice" v-if="userHasReadPermission(PermissionTypes.AccountsPermissionTypes.invoice) && contactData.isCustomer">
                  <div class="row">
                    <div class="col-lg-12">
                      <InvoiceDataTable :filterContactId="id" :openNewTab="true">
                        <template v-slot:title>
                          <h3>Invoice List for {{contactData.name}}</h3>
                        </template>
                      </InvoiceDataTable>
                    </div>
                  </div>
                </TabPage>
                <TabPage tabid="tab-customer-transaction-payment" v-if="userHasReadPermission(PermissionTypes.AccountsPermissionTypes.payment) && contactData.isCustomer">
                  <div class="row">
                    <div class="col-lg-12">
                      <PaymentReceivedDataTable :filterContactId="id" :openNewTab="true">
                        <template v-slot:title>
                          <h3>Payment Received List for {{contactData.name}}</h3>
                        </template>
                      </PaymentReceivedDataTable>
                    </div>
                  </div>
                </TabPage>
                <TabPage tabid="tab-customer-transaction-overpayment" v-if="userHasReadPermission(PermissionTypes.AccountsPermissionTypes.payment) && contactData.isCustomer">
                  <div class="row">
                    <div class="col-lg-12">
                      <OverPaymentDataTable :filterContactId="id" :openNewTab="true">
                        <template v-slot:title>
                          <h3>Over Payment List for {{contactData.name}}</h3>
                        </template>
                      </OverPaymentDataTable>
                    </div>
                  </div>
                </TabPage>
                <TabPage tabid="tab-customer-transaction-creditnote" v-if="userHasReadPermission(PermissionTypes.AccountsPermissionTypes.creditNote) && contactData.isCustomer">
                  <div class="row">
                    <div class="col-lg-12">
                      <CreditNoteDataTable :filterContactId="id" :openNewTab="true">
                        <template v-slot:title>
                          <h3>Credit Note List for {{contactData.name}}</h3>
                        </template>
                      </CreditNoteDataTable>
                    </div>
                  </div>
                </TabPage>
                <TabPage :first="!contactData.isCustomer" tabid="tab-customer-transaction-po" v-if="userHasReadPermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrder) && contactData.isSupplier">
                  <div class="row">
                    <div class="col-lg-12">
                      <PurchaseOrderDataTable :filterContactId="id" :openNewTab="true">
                        <template v-slot:title>
                          <h3>Purchase Order List for {{contactData.name}}</h3>
                        </template>
                      </PurchaseOrderDataTable>
                    </div>
                  </div>
                </TabPage>
              </div>
            </TabPage>

            <TabPage tabid="tab-9">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityLog" :url="activityStreamURL" :linkType="LinkTypes.contact" :linkId="id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import Vue from 'vue'
import { mapState } from "vuex"
import moment from 'moment'

import Utils from '@/utils/utils'
import pdfMixin from '@/mixins/pdf.js'
import pageMixin from '@/mixins/page.js'
import StaticDataTable from "@/components/table/staticDataTable.vue";
import DocumentList from "@/components/documentList.vue";
import EventBus from '@/utils/eventBus'

import SupplierBulkPriceList from '@/modules/pricing/components/supplierBulkPriceList'
import SupplierProcessedPriceList from '@/modules/pricing/components/supplierProcessedPriceList'
import SupplierServicePriceList from '@/modules/pricing/components/supplierServicePriceList'
import SupplierGeneralPriceList from '@/modules/pricing/components/supplierGeneralPriceList'

import SupplierPriceRequestUpdateDialog from '@/modules/pricing/views/supplierPriceRequestUpdateDialog.vue'

import ContactAddressDialog from '@/modules/contacts/views/contactAddressDialog.vue'
import ContactPersonDialog from '@/modules/contacts/views/contactPersonDialog.vue'
import ContactEditDialog from '@/modules/contacts/views/contactEditDialog.vue'
import ContactEditDespatchDialog from '@/modules/contacts/views/contactEditDespatchDialog.vue'
import ContactEditCreditStatusDialog from '@/modules/contacts/views/contactEditCreditStatusDialog.vue'
import ContactEditCreditDialog from '@/modules/contacts/views/contactEditCreditDialog.vue'
import ContactEditSupplierDialog from '@/modules/contacts/views/contactEditSupplierDialog.vue'
import EditLogoImageDialog from '@/modules/core/views/editLogoImageDialog.vue'
import CustomerPriceListSetupDialog from '@/modules/pricing/views/customerPriceListSetupDialog'
import ContactEditPricingDialog from '@/modules/contacts/views/contactEditPricingDialog'

import OrderDataTable from '@/modules/orders/components/orderDataTable'
import InvoiceDataTable from '@/modules/accounts/components/invoiceDataTable'
import PaymentReceivedDataTable from '@/modules/accounts/components/paymentReceivedDataTable'
import OverPaymentDataTable from '@/modules/accounts/components/overPaymentDataTable'
import CreditNoteDataTable from '@/modules/accounts/components/creditNoteDataTable'
import PurchaseOrderDataTable from '@/modules/purchasing/components/purchaseOrderDataTable'

import AnalyticsContactDashboard from '@/modules/intelligence/components/analyticsContactDashboard'
import SendContactStatementDialog from '@/modules/accounts/views/sendContactStatementDialog'

import XeroContactRelinkDialog from '@/integrations/xero/views/xeroContactRelinkDialog'

import * as Types from '@/store/types'

export default {
  name: "Contact",
  components: {
    StaticDataTable,
    DocumentList,
    SupplierBulkPriceList,
    SupplierProcessedPriceList,
    SupplierServicePriceList,
    SupplierGeneralPriceList,
    
    OrderDataTable,
    InvoiceDataTable,
    PaymentReceivedDataTable,
    OverPaymentDataTable,
    CreditNoteDataTable,
    PurchaseOrderDataTable,

    AnalyticsContactDashboard
  },
  mixins: [pageMixin, pdfMixin],
  data: () => {
    return {
      breadcrumbs: [
        { title: "Contacts", link: "/contacts" },
        { title: "Contact" }
      ],
      loading: false,
      contactData: {
        credit: {}
      },
      supplierBulkPricingData: {},
      editingBulkPricing: false,
      bulkPricingSubmitting: false,

      supplierProcessedPricingData: {},
      editingProcessedPricing: false,
      processedPricingSubmitting:false,

      supplierServicePricingData: {},
      editingServicePricing: false,
      servicePricingSubmitting:false,

      supplierGeneralPricingData: {},
      editingGeneralPricing: false,
      generalPricingSubmitting:false,

      customerPriceLists: [],
    };
  },
  computed: {
    ...mapState({
      accountPricingSystemType: state => state.account.pricingSystemType
    }),
    id() {
      return parseInt(this.$route.params.id)
    },
    creditInfoLinkType() {
      return Types.LinkTypes.contactCreditInfo
    },
    defaultContact() {
      if (this.$data.contactData.defaultPersonId == null) return '';

      var defaultPerson = this.$data.contactData.persons.filter(
        person => person.id == this.$data.contactData.defaultPersonId
      )[0]
      return `${defaultPerson.firstName} ${defaultPerson.lastName}`
    },
    defaultAddress() {
      if (this.$data.contactData.defaultAddressId == null) return {};
      return this.$data.contactData.addresses.filter(
        address => address.id == this.$data.contactData.defaultAddressId
      )[0];
    },
    defaultSalesRate() {
      if(this.$data.contactData.defaultSalesRate == null)
        return 'None'
      return this.$data.contactData.defaultSalesRate.name
    },
    logoURL() {
      if(this.$data.contactData.logoURL == null || this.$data.contactData.logoURL == '') {
        var glassImgPath = '/glass/img'
        if(this.$data.contactData.isSupplier)
          return `${glassImgPath}/contact-overview-supplier.png`
        if(this.$data.contactData.isCustomer)
          return `${glassImgPath}/contact-overview-customer-2.png`
        return ''
      }
      return this.$data.contactData.logoURL
    },
    activityStreamURL() {
      return `/contacts/${this.$route.params.id}/activitystream`
    },
    creditActivityStreamURL() {
      return `/contacts/${this.$route.params.id}/credit/activitystream`
    },
    isCreditTypeAccount() {
      if(this.$data.contactData.credit == null)
        return false
      return this.$data.contactData.credit.creditType == Types.CreditTypes.account
    },
    creditType() {
      return Utils.getLookupName(Types.CreditTypeLookup, this.$data.contactData.credit.creditType);
    },
    creditStatus() {
      return Utils.getLookupName(Types.CreditStatusLookup, this.$data.contactData.credit.creditStatus);
    },
    creditTypeName() {
      switch(this.$data.contactData.credit.creditType) {
        case Types.CreditTypes.account: {
          var name = 'Account - '
          if(this.$data.contactData.credit.creditStatus == Types.CreditStatusTypes.active)
            name += 'Active'
          else if(this.$data.contactData.credit.creditStatus == Types.CreditStatusTypes.onhold)
            name += 'On Hold'
          else if(this.$data.contactData.credit.creditStatus == Types.CreditStatusTypes.stop)
            name += 'Stopped'

          return name
        }
        case Types.CreditTypes.prepaid: return 'Pre-paid'
        case Types.CreditTypes.cod: return 'Cash on Delivery'
        default: return 'Unknown'
      }
    },
    creditTypeClass() {
      switch(this.$data.contactData.credit.creditType) {
        case Types.CreditTypes.account:
          return `status-creditstatustype-${this.$data.contactData.credit.creditStatus}`
          
        default: return ''
      }      
    },
    creditPaymentTerms() {
      var termsInfo = this.$data.contactData.credit.paymentTermsAmount
      termsInfo += ' ' + Utils.getLookupName(Types.PaymentTermLookup, this.$data.contactData.credit.paymentTermsType)
      return termsInfo
    },
    entityType() {
      return Utils.getLookupName(Types.ContactEntityLookup, this.$data.contactData.credit.entityType);
    },
    supplierType() {
      return Utils.getLookupName(Types.SupplierTypeLookup, this.$data.contactData.supplierType);
    },
    supplierPaymentTerms() {
      switch(this.$data.contactData.supplierCreditType) {
        case Types.CreditTypes.account:
          var termsInfo = this.$data.contactData.supplierPaymentTermsAmount
          termsInfo += ' ' + Utils.getLookupName(Types.PaymentTermLookup, this.$data.contactData.supplierPaymentTermsType)
          return termsInfo

        case Types.CreditTypes.prepaid: return 'Pre-paid'
        case Types.CreditTypes.cod: return 'Cash on Delivery'
      }      

      return ''
    },
    supplierLandingCost() {
      var amount = ''
      if(this.$data.contactData.landingCostType == Types.DeliveryPricingTypes.fixed)
        amount = this.$options.filters.formatCurrency(this.$data.contactData.landingFixedValue)
      if(this.$data.contactData.landingCostType == Types.DeliveryPricingTypes.percent)
        amount = this.$options.filters.formatPercent(this.$data.contactData.landingPercentValue)
      
      return amount
    },
    supplierDeliveryLead() {
      if(this.$data.contactData.supplierDeliveryLeadDays == null)
        return ''
      return this.$data.contactData.supplierDeliveryLeadDays + ' day(s)'
    },
    supplierTaxType() {
      if(this.$data.contactData.supplierTaxRate == null)
        return 'None'
      return this.$data.contactData.supplierTaxRate.name
    },
    hasIntegrations() {
      return this.integrationList.filter(integration => integration.active).length > 0
    },
    integrationList() {
      if(this.$data.contactData.integrationData == null)
        return []
      return Object.values(this.$data.contactData.integrationData)        
    },
    customerCurrentPriceLists() {
      if(this.$data.customerPriceLists == null)
        return []
      return this.$data.customerPriceLists.filter(priceList => !priceList.isArchived)
    },
    customerCompletePriceLists() {
      if(this.$data.customerPriceLists == null)
        return []

      var archivedList = this.$data.customerPriceLists.filter(priceList => priceList.isArchived)

      // Sort by most recent first
      archivedList.sort((list1, list2) => {
        if(!list1.snapshotStartDate)
          return 1
        if(!list2.snapshotStartDate)
          return -1

        return moment(list2.snapshotStartDate).unix() - moment(list1.snapshotStartDate).unix()
      }) 

      return archivedList
    },
    isCustomerPricingAvailable() {
      if(!this.contactData.isCustomer)
        return false
      if(!this.userHasReadPermission(this.PermissionTypes.ContactPermissionTypes.customerPricing))
        return false
      if(this.accountPricingSystemType == Types.PricingSystemTypes.simple)
        return false
      return true
    },
    isAnalyticsDashboardAvailable() {
      if(this.userHasReadPermission(this.PermissionTypes.IntelligencePermissionTypes.intelligenceAccount) || this.userHasReadPermission(this.PermissionTypes.IntelligencePermissionTypes.intelligenceOrder))
        return true
      return false
    }
  },
  created() {
    this.LinkTypes = Types.LinkTypes
    this.DespatchTypes = Types.DespatchTypes
    this.DespatchTypeLookup = Types.DespatchTypeLookup
  },
  mounted() {
    EventBus.on(EventBus.EventTypes.permissionsUpdated, this.permissionsUpdated)
    if(this.isPermissionDataLoaded()) {
      this.permissionsUpdated()
    }
    this.requestData()
  },
  beforeRouteUpdate (to, from, next) {
    next()
    this.pageRefreshed()
    this.requestData()
  },
  beforeDestroy() {
    EventBus.off(EventBus.EventTypes.permissionsUpdated, this.permissionsUpdated)
  },
  methods: {
    shouldShowLegalName() {
      if(this.contactData.credit.entityType == Types.ContactEntityTypes.none || (this.contactData.credit.entityType == Types.ContactEntityTypes.trust && !this.contactData.credit.isCorporateTrustee))
        return false
      return true
    },
    shouldShowCompanyFields() {
      if(this.contactData.credit.entityType == Types.ContactEntityTypes.company || (this.contactData.credit.entityType == Types.ContactEntityTypes.trust && this.contactData.credit.isCorporateTrustee))
        return true
      return false
    },
    isCreditTrustType() {
      return this.contactData.credit.entityType == Types.ContactEntityTypes.trust
    },
    isCreditPropertySecured() {
      return this.contactData.credit.isPersonalPropertySecured
    },
    permissionsUpdated() {
      if(!this.isAnalyticsDashboardAvailable) {
        this.$nextTick(() => {
          this.$refs.contactDetailTab.show()
        })
      }
    },

    reloadData() {
      this.$refs.activityLog.reloadData()
      if(this.$refs.creditActivityLog)
        this.$refs.creditActivityLog.reloadData()
      this.requestData()
    },
    requestData() {
      this.loading = true

      this.$apiService.contactService.getContact(this.$route.params.id)
      .then(response => {
        this.$data.contactData = response.data

        this.$store.commit("page/setPageTitle", this.$data.contactData.name)

        // Customer Pricing details
        if(this.$data.contactData.isCustomer) {
          this.$apiService.pricingService.getCustomerPriceLists(this.$route.params.id).then(response => {
            this.$data.customerPriceLists = response.data
          })
        }

        // Supplier Pricing details
        if(this.$data.contactData.isSupplier) {
          this.$apiService.pricingService.getSupplierBulkPricingList(this.$route.params.id).then(response => {
            this.$data.supplierBulkPricingData = response.data
          })
          this.$apiService.pricingService.getSupplierProcessedPricingList(this.$route.params.id).then(response => {
            this.$data.supplierProcessedPricingData = response.data
          })
          this.$apiService.pricingService.getSupplierServicePricingList(this.$route.params.id).then(response => {
            this.$data.supplierServicePricingData = response.data
          })
          this.$apiService.pricingService.getSupplierGeneralPricingList(this.$route.params.id).then(response => {
            this.$data.supplierGeneralPricingData = response.data
          })
        }
      })
      .finally(() => {
        this.loading = false
      })
    },
    newAddress() {
      this.displayModal(ContactAddressDialog, { 
        contactId: this.$data.contactData.id,
        isCreateMode: true
      })
      .then(contactAddressId => {
        if(contactAddressId) {
          this.successToast('Address Added')
          this.reloadData()
        }
      })
    },
    newPerson() {
      this.displayModal(ContactPersonDialog, { 
        contactId: this.$data.contactData.id,
        isCreateMode: true
      })
      .then(contactPersonId => {
        if(contactPersonId) {
          this.successToast('Person Added')
          this.reloadData()
        }
      })
    },
    
    editContact() {
      this.displayModal(ContactEditDialog, { 
        contactId: this.$data.contactData.id,
      },
      this.$data.contactData)
      .then((success) => {
        if(success) {
          this.successToast('Contact Updated')
          this.reloadData()
        }
      })
    },
    editDespatch() {
      this.displayModal(ContactEditDespatchDialog, { 
        contactId: this.$data.contactData.id,
      },
      this.$data.contactData)
      .then((success) => {
        if(success) {
          this.successToast('Contact Despatch Updated')
          this.reloadData()
        }
      })
    },
    archiveContact() {
      this.confirmDeleteMessage('Archive Contact?', 'Are you sure you want to archive this contact?')
      .then(success => {
        if(success) {
          this.$apiService.contactService.archiveContact(this.$route.params.id).then(response => {
            this.successToast('Contact Archived')
            this.reloadData()
          })
          // TODO: Test error response
        }
      })
    },
    restoreContact() {
      this.confirmDeleteMessage('Restore Contact?', 'Are you sure you want to restore this contact?')
      .then(success => {
        if(success) {
          this.$apiService.contactService.restoreContact(this.$route.params.id).then(response => {
            this.successToast('Contact Restored')
            this.reloadData()
          })
          // TODO: Test error response
        }
      })
    },
    deleteContact(contactId) {
      this.confirmDeleteMessage('Delete Contact?', 'Are you sure you want to delete this contact?')
      .then(success => {
        if(success) {
          this.$apiService.contactService.deleteContact(this.$route.params.id).then(response => {
            this.successToast('Contact Deleted')
            this.$router.push(`/contacts`)
          })
          // TODO: Test error response
        }
      })
    },


    editAddress(address) {
      this.displayModal(ContactAddressDialog, { 
        contactId: this.$data.contactData.id,
        isCreateMode: false
      },
      address)
      .then(contactAddressId => {
        if(contactAddressId) {
          this.successToast('Address Updated')
          this.reloadData()
        }
      })
    },
    deleteAddress(addressId) {
      this.confirmDeleteMessage('Delete Address?', 'Are you sure you want to delete this address?')
      .then(success => {
        if(success) {
          this.$apiService.contactService.deleteContactAddress(this.$route.params.id, addressId)
          .then(response => {
            this.successToast('Address Deleted')
            this.reloadData()
          })
          // TODO: Test error response
        }
      })
    },
    updateDefaultAddress(addressId) {
      this.$apiService.contactService.updateContactDefaultAddress(this.$route.params.id, addressId)
      .then(response => {
        this.successToast('Default Address Set')
        this.reloadData()
      })
    },

    editPerson(person) {
      this.displayModal(ContactPersonDialog, { 
        contactId: this.$data.contactData.id,
        isCreateMode: false
      }, person)
      .then(contactPersonId => {
        if(contactPersonId) {
          this.successToast('Person Updated')
          this.reloadData()
        }
      })
    },    
    deletePerson(personId) {
      this.confirmDeleteMessage('Delete Person?', 'Are you sure you want to delete this person?')
      .then(success => {
        if(success) {
          this.$apiService.contactService.deleteContactPerson(this.$route.params.id, personId)
          .then(response => {
            this.successToast('Person Deleted')
            this.reloadData()            
          })
          // TODO: Test error response
        }
      })
    },
    updateDefaultPerson(personId) {
      this.$apiService.contactService.updateContactDefaultPerson(this.$route.params.id, personId)
      .then(response => {
        this.successToast('Default Person Set')
        this.reloadData()
      })
    },

    editCreditStatus() {
      this.displayModal(ContactEditCreditStatusDialog, { 
        contactId: this.$data.contactData.id,
      },
      this.$data.contactData.credit)
      .then((success) => {
        if(success) {
          this.successToast('Credit Status Updated')
          this.reloadData()
        }
      })
    },
    editCreditDetails() {
      this.displayModal(ContactEditCreditDialog, { 
        contactId: this.$data.contactData.id,
      },
      this.$data.contactData.credit)
      .then((success) => {
        if(success) {
          this.successToast('Credit Details Updated')
          this.reloadData()
        }
      })
    },
    editSupplierDetails() {
      this.displayModal(ContactEditSupplierDialog, { 
        contactId: this.$data.contactData.id,
      },
      this.$data.contactData)
      .then((success) => {
        if(success) {
          this.successToast('Supplier Details Updated')
          this.reloadData()
        }
      })
    },

    editAvatarImage() {
      this.displayModal(EditLogoImageDialog, { 
        resourceId: this.$data.contactData.id,
        APIresource: 'contacts',
        currentURL: this.$data.contactData.logoURL
      })
      .then((success) => {
        if(success) {
          this.successToast('Logo Updated')
          this.reloadData()
        }
      })
    },
    removeAvatarImage() {
      this.confirmDeleteMessage('Remove Image?', 'Are you sure you remove this contact\'s image?')
      .then(success => {
        if(success) {
          this.$apiService.coreService.deleteLogoImage(this.$data.contactData.id, 'contacts').then(response => {
            this.successToast('Image Removed')
            this.reloadData()
          })
        }
      })
    },

    // Supplier Pricing
    displaySupplierPricingReport() {
      this.displayReportPDF(`/contacts/${this.$data.contactData.id}/supplier/pricing/report`)
    },
    cancelPriceEditing() {
      this.editingBulkPricing = false
      this.editingProcessedPricing = false
      this.editingServicePricing = false
      this.editingGeneralPricing = false

      // Refresh data
      this.reloadData()
    },

    submitBulkPricingEdit() {
      this.displayModal(SupplierPriceRequestUpdateDialog, {
        priceType: Types.StockPriceTypes.bulkContainer,
        contactId: this.$data.contactData.id
      }, this.$data.supplierBulkPricingData)
      .then((success) => {
        if(success) {
          this.successToast('Pricing Request Submitted')
          this.editingBulkPricing = false

          // Refresh data back to current prices
          this.reloadData()
        }
      })
    },
    submitProcessedPricingEdit() {
      this.displayModal(SupplierPriceRequestUpdateDialog, {
        priceType: Types.StockPriceTypes.processedPriceGroup,
        contactId: this.$data.contactData.id
      }, this.$data.supplierProcessedPricingData)
      .then((success) => {
        if(success) {
          this.successToast('Pricing Request Submitted')
          this.editingProcessedPricing = false

          // Refresh data back to current prices
          this.reloadData()
        }
      })
    },

    submitServicePricingEdit() {
      this.displayModal(SupplierPriceRequestUpdateDialog, {
        priceType: Types.StockPriceTypes.servicePriceRange,
        contactId: this.$data.contactData.id
      }, this.$data.supplierServicePricingData)
      .then((success) => {
        if(success) {
          this.successToast('Pricing Request Submitted')
          this.editingServicePricing = false

          // Refresh data back to current prices
          this.reloadData()
        }
      })
    },

    submitGeneralPricingEdit() {
      this.displayModal(SupplierPriceRequestUpdateDialog, {
        priceType: Types.StockPriceTypes.general,
        contactId: this.$data.contactData.id
      }, this.$data.supplierGeneralPricingData)
      .then((success) => {
        if(success) {
          this.successToast('Pricing Request Submitted')
          this.editingGeneralPricing = false

          // Refresh data back to current prices
          this.reloadData()
        }
      })
    },


    // Customer Pricing
    editCustomerPricingDetails() {
      this.displayModal(ContactEditPricingDialog, {
        contactId: this.$data.contactData.id
      }, this.contactData)
      .then((result) => {
        if(result) {
          this.successToast('Customer pricing details updated')
          this.reloadData()
        }
      })
    },

    newCustomerPriceList() {
      // Display the dialog to setup a new price list
      this.displayModal(CustomerPriceListSetupDialog)
      .then((result) => {
        if(result) {
          // Start new price list editing with the integration type
          this.$router.push(`/pricing/customer/${this.$data.contactData.id}/pricelist/new?priceListIntegrationType=${result}`)
        }
      })
    },
    viewCustomerPriceListConfig() {
      this.$router.push(`/pricing/customer/${this.$data.contactData.id}/pricelistconfig`)
    },
    displayCustomerPriceList(priceListId) {
      this.$router.push(`/pricing/customer/${this.$data.contactData.id}/pricelist/${priceListId}`)
    },
    displayCustomerPriceListReport(priceListId) {
      this.displayReportPDF(`/contacts/${this.$data.contactData.id}/customer/pricelist/${priceListId}/report`)
    },


    // Integration
    relinkIntegration(relinkData) {

      switch(relinkData.integration.code) {
        case Types.IntegrationTypeCodes.xero:
          this.displayModal(XeroContactRelinkDialog, {
            contactId: this.contactData.id,
            xeroContactID: relinkData.integration.contactID
          })
          .then((result) => {
            if(result) {
              this.reloadData()
            }
          })

          break
      }
    },

    // Accounts
    sendAccountStatement() {
      this.displayModal(SendContactStatementDialog, { 
        contactId: parseInt(this.contactData.id)
      })
      .then((success) => {
        if(success) {
          this.successToast('Statement Sent')
          this.reloadData()
        }
      })
    }

  }
};
</script>
